<template>
  <div class="video-wrapper">
    <video
      class="video-component"
      :class="{'video-hidden': hideVideoUntilPlaying}"
      ref="refVideo"
      :src="videoPath"
      :loop="checkLoop"
      v-video="play"
      :autoplay="play"
      :muted="isMuted"
      preload="auto"
      playsinline
      @click="handleManualPlay"
    ></video>
    <div v-if="showLoaderOverlay" class="video-loader-overlay">
      <div class="video-loader-spinner"></div>
    </div>
    <button
      v-if="showPlayOverlayButton"
      class="play-button"
      @click="handleManualPlay"
    >
      ▶︎ 再生
    </button>
    <button
      v-if="showSoundButton && isMuted"
      class="sound-on-button"
      @click="handleSoundOn"
    >
      サウンドON
    </button>
    <button
      v-if="showMuteIconButton"
      class="mute-toggle-button"
      :aria-label="isMuted ? 'サウンドON' : 'サウンドOFF'"
      @click="toggleMute"
    >
      <template v-if="isMuted">
        <img v-if="muteIconSrc" :src="muteIconSrc" alt="muted" class="mute-icon-img" />
        <svg v-else width="28" height="28" viewBox="0 0 24 24" fill="none">
          <rect x="0" y="0" width="24" height="24" rx="12" fill="rgba(0,0,0,0.5)"/>
          <path d="M3 10v4h3l4 4V6L6 10H3z" fill="#fff"/>
          <path d="M16 7c1.66 1.66 1.66 8.34 0 10" stroke="#fff" stroke-width="2" stroke-linecap="round"/>
          <path d="M19 4L5 18" stroke="#fff" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </template>
      <template v-else>
        <img v-if="unmuteIconSrc" :src="unmuteIconSrc" alt="unmuted" class="mute-icon-img" />
        <svg v-else width="28" height="28" viewBox="0 0 24 24" fill="none">
          <rect x="0" y="0" width="24" height="24" rx="12" fill="rgba(0,0,0,0.5)"/>
          <path d="M3 10v4h3l4 4V6L6 10H3z" fill="#fff"/>
          <path d="M16 7c2.5 2.5 2.5 7.5 0 10" stroke="#fff" stroke-width="2" stroke-linecap="round"/>
          <path d="M18.5 5.5c3.5 3.5 3.5 9.5 0 13" stroke="#fff" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </template>
    </button>
  </div>
</template>

<script>
export default {
  props: {
    videoPath: String,
    checkLoop: {
      type: Boolean,
      default: false
    },
    play: {
      type: Boolean,
      default: true
    },
    muted: {
      type: Boolean,
      default: true
    },
    showSoundButton: {
      type: Boolean,
      default: false
    },
    showPlayOverlay: {
      type: Boolean,
      default: false
    },
    // 結果動画など、終了後は再生ボタンを再表示しない
    hideOverlayAfterEnd: {
      type: Boolean,
      default: false
    },
    // ミュートトグルアイコンの表示
    showMuteToggleIcon: {
      type: Boolean,
      default: false
    },
    // 結果動画など、終了後はミュートアイコンも非表示にする
    hideMuteIconAfterEnd: {
      type: Boolean,
      default: false
    },
    // 動画領域のクリックで手動再生を許可するか
    allowManualPlay: {
      type: Boolean,
      default: true
    },
    // 終了後に再度の手動再生（リプレイ）を許可するか
    allowReplayAfterEnd: {
      type: Boolean,
      default: true
    },
    // 読み込み中オーバーレイの表示
    showLoadingOverlay: {
      type: Boolean,
      default: true
    },
    // カスタムアイコン（ミュート・アンミュート）
    muteIconSrc: {
      type: String,
      default: ""
    },
    unmuteIconSrc: {
      type: String,
      default: ""
    }
  },
  computed: {
    showLoaderOverlay() {
      // 実フレームが描画されるまでローディングを表示する
      return this.showLoadingOverlay && !this.hasRenderedFrame;
    },
    hideVideoUntilPlaying() {
      // 実フレーム描画まで video を不可視（Android WebView のポスタープレースホルダ対策）
      return !this.hasRenderedFrame;
    },
    showPlayOverlayButton() {
      // 再生オーバーレイの表示条件:
      // - オーバーレイ機能が有効
      // - 再生中ではない
      // - hideOverlayAfterEnd=true の場合は、終了後は非表示
      return (
        this.showPlayOverlay &&
        !this.isPlaying &&
        !(this.hideOverlayAfterEnd && this.hasEnded)
      );
    },
    showMuteIconButton() {
      return (
        this.showMuteToggleIcon &&
        !(this.hideMuteIconAfterEnd && this.hasEnded)
      );
    }
  },
  data() {
    return {
      isMuted: this.muted,
      isPlaying: false,
      hasEnded: false,
      hasFirstFrame: false,
      isBuffering: false,
      hasRenderedFrame: false
    };
  },
  watch: {
    muted(newVal) {
      this.isMuted = newVal;
    },
    play(newVal) {
      if (newVal && this.hasFirstFrame) {
        this.isBuffering = true;
      }
    },
    videoPath() {
      this.resetBeforeLoad();
      if (this.noCache && !this.cacheBustKey) {
        this.cacheBustValue = String(Date.now());
      }
    },
    cacheBustKey() {
      // 外部からキーを差し替えた場合も更新
      if (this.noCache && this.cacheBustKey) {
        this.cacheBustValue = String(this.cacheBustKey);
      }
      this.resetBeforeLoad();
    }
  },
  // カスタムディレクティブで動画の再生をv-videoででき、playのbool値で動画の再生の判定している
  directives: {
    video(el, binding) {
      binding.value ? el.play() : el.pause();
    }
  },
  //refで指定したDOMが存在する場合に動画の再生終了時にendedイベント取得し、videoEndedメソッドを呼び出す。
  mounted() {
    const videoElement = this.$refs.refVideo;
    if (videoElement) {
      videoElement.muted = this.isMuted;
      videoElement.addEventListener("ended", this.videoEnded);
      videoElement.addEventListener("loadeddata", () => {
        this.hasFirstFrame = true;
        if (!this.play) {
          this.isBuffering = false;
        }
      });
      videoElement.addEventListener("canplay", () => {
        this.hasFirstFrame = true;
        if (!this.play) {
          this.isBuffering = false;
        }
      });
      videoElement.addEventListener("play", () => {
        this.isPlaying = true;
        this.hasEnded = false;
        this.isBuffering = false;
      });
      videoElement.addEventListener("pause", () => {
        this.isPlaying = false;
      });
      const markRendered = () => {
        if (!this.hasRenderedFrame) {
          if (videoElement.currentTime > 0 || videoElement.readyState >= 2) {
            this.hasRenderedFrame = true;
          }
        }
      };
      videoElement.addEventListener("timeupdate", markRendered);
      videoElement.addEventListener("seeked", markRendered);
      videoElement.addEventListener("playing", markRendered);
      videoElement.addEventListener("waiting", () => {
        this.isBuffering = true;
      });
      videoElement.addEventListener("stalled", () => {
        this.isBuffering = true;
      });
      this.isBuffering = !!this.play;
    }
  },
  //動画終了時に親コンポーネントのendVideoでバインドされた関数を実行する
  methods: {
    resetBeforeLoad() {
      this.hasRenderedFrame = false;
      this.hasFirstFrame = false;
      this.isPlaying = false;
      this.isBuffering = !!this.play;
    },
    videoEnded() {
      this.hasEnded = true;
      this.isBuffering = false;
      this.$emit("endVideo");
    },
    handleManualPlay() {
      // クリック再生の可否を制御
      if (!this.allowManualPlay) return;
      if (this.hasEnded && !this.allowReplayAfterEnd) return;
      this.isBuffering = true;
      const videoElement = this.$refs.refVideo;
      if (videoElement) {
        videoElement.muted = this.isMuted;
        const playPromise = videoElement.play();
        if (playPromise && typeof playPromise.then === "function") {
          playPromise.then(() => {
            this.$emit("requestPlay");
          }).catch(() => {
            // 再生失敗時も親に通知（親でplayフラグを立て直すため）
            this.$emit("requestPlay");
          });
        } else {
          this.$emit("requestPlay");
        }
      } else {
        this.$emit("requestPlay");
      }
    },
    handleSoundOn() {
      this.isMuted = false;
      const videoElement = this.$refs.refVideo;
      if (videoElement) {
        videoElement.muted = false;
        videoElement.play();
      }
      this.$emit("soundOn");
    },
    toggleMute() {
      this.isMuted = !this.isMuted;
      const videoElement = this.$refs.refVideo;
      if (videoElement) {
        videoElement.muted = this.isMuted;
      }
    }
  }
};
</script>

<style lang="css" scoped>
.video-wrapper {
  position: relative;
}
.video-component {
  width: 100%;
  z-index: 1;
  transition: opacity 0.12s ease-in;
}
.video-component.video-hidden {
  opacity: 0;
  pointer-events: none;
}
.video-loader-overlay {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  z-index: 2;
}
.video-loader-spinner {
  width: 56px;
  height: 56px;
  border: 5px solid rgba(255, 255, 255, 0.45);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: video-spin 1s linear infinite;
  box-shadow: 0 0 8px rgba(0,0,0,0.25);
}
@keyframes video-spin {
  to {
    transform: rotate(360deg);
  }
}
.play-button {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  padding: 10px 16px;
  border: none;
  border-radius: 24px;
  background-color: rgba(0, 0, 0, 0.6);
  color: #ffffff;
  font-weight: bold;
}
.sound-on-button {
  position: absolute;
  right: 12px;
  bottom: 12px;
  padding: 8px 12px;
  border: none;
  border-radius: 6px;
  background-color: #007bff;
  color: #ffffff;
  font-weight: bold;
}
.mute-toggle-button {
  position: absolute;
  left: 12px;
  bottom: 12px;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  background: transparent;
}
</style>



// WEBPACK FOOTER //
// src/components/campaign/CampaignVideo.vue